import React, { useEffect, useState } from 'react';
import useDialog from '../utils/useDialog';
import {
  ANALYTICS_BUILD_ENABLED,
  getAnalyticsConsent,
  grantAnalyticsConsent,
  denyAnalyticsConsent,
} from '../firebase';

/**
 * ConsentBanner — a small opt-out cookie notice.
 *
 * Consent model is OPT-OUT (chosen for this product): analytics is already
 * running when this shows, and continued use counts as agreement. The banner
 * states this in plain language and offers an explicit "No thanks" that stops
 * collection. It appears only while no explicit choice has been recorded
 * (and only when analytics is build-enabled).
 */
export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (ANALYTICS_BUILD_ENABLED && getAnalyticsConsent() === null) {
      setVisible(true);
    }
  }, []);

  const accept = () => {
    grantAnalyticsConsent();
    setVisible(false);
  };

  const decline = () => {
    denyAnalyticsConsent();
    setVisible(false);
  };

  // Keyboard support (UI_REVIEW §1): Escape dismisses the banner. On a kids'
  // app we treat Escape as the privacy-safe choice — decline. trapFocus is off
  // because this is a non-modal banner; the page behind stays usable.
  const dialogRef = useDialog({ onClose: decline, trapFocus: false, active: visible });

  if (!visible) return null;

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      style={styles.bar}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie notice"
    >
      <span style={styles.icon} aria-hidden="true">🍪</span>
      <p style={styles.text}>
        We use cookies to help make Orboot better. By continuing, you agree —
        or you can opt out.
      </p>
      <div style={styles.actions}>
        <button onClick={decline} style={styles.decline}>No thanks</button>
        <button onClick={accept} style={styles.accept}>Got it</button>
      </div>
    </div>
  );
}

const styles = {
  bar: {
    position: 'fixed',
    left: 12,
    right: 12,
    bottom: 12,
    zIndex: 1200,
    margin: '0 auto',
    maxWidth: 560,
    background: 'rgba(255,255,255,0.98)',
    borderRadius: 14,
    boxShadow: '0 10px 30px rgba(0,0,0,0.22)',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
  },
  icon: { fontSize: 20, flexShrink: 0 },
  text: {
    flex: 1,
    minWidth: 0,
    margin: 0,
    fontSize: 13,
    lineHeight: 1.4,
    color: '#374151',
  },
  actions: { display: 'flex', gap: 8, flexShrink: 0 },
  decline: {
    height: 34,
    padding: '0 12px',
    borderRadius: 9,
    border: 'none',
    background: 'transparent',
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  accept: {
    height: 34,
    padding: '0 16px',
    borderRadius: 9,
    border: 'none',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    color: '#1f2937',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },
};
